import React,{ useCallback,useEffect,useState } from 'react';
import useWebSocket from '../../hooks/useWebSocket';
import AutomationCard from './AutomationCard';
import './AutomationList.css';
import { useAuth } from '../../context/AuthContext';

const AutomationList = () => {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { apiRequest } = useAuth();
  const API_BASE_URL = '';
  const API_PATH = '/api';
  const API_WS_URL = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    try {
      // Parse the message if it's a string
      const message = typeof data === 'string' ? JSON.parse(data) : data;
      
      // Handle automation events
      if (message.type === 'automation-triggered') {
        // Update the triggered automation in the list
        setAutomations(prevAutomations => 
          prevAutomations.map(automation => 
            automation.id === message.data.automationId 
              ? { ...automation, lastTriggered: new Date().toISOString() } 
              : automation
          )
        );
      } else if (message.type === 'automation-created') {
        // Add the new automation to the list
        setAutomations(prevAutomations => 
          [...prevAutomations, message.data.automation]
        );
      } else if (message.type === 'automation-updated') {
        // Update the automation in the list
        setAutomations(prevAutomations => 
          prevAutomations.map(automation => 
            automation.id === message.data.automationId 
              ? message.data.automation 
              : automation
          )
        );
      } else if (message.type === 'automation-deleted') {
        // Remove the automation from the list
        setAutomations(prevAutomations => 
          prevAutomations.filter(automation => 
            automation.id !== message.data.automationId
          )
        );
      }
    } catch (err) {
      console.error('Error processing WebSocket message:', err);
    }
  }, []);

  // const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // const wsUrl = `${protocol}/automations`;


  const getToken = () => localStorage.getItem('authToken'); // or from Redux/context

  useWebSocket(`${API_WS_URL}/ws/automations`, handleWebSocketMessage, getToken);
  // Set up WebSocket connection

  // Fetch automations from the API
  useEffect(() => {
    const fetchAutomations = async () => {
      try {
        setLoading(true);
        const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations`);
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        
        // const data = await response.json();
        setAutomations(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching automations:', err);
        setError('Failed to load automations');
      } finally {
        setLoading(false);
      }
    };

    fetchAutomations();
  }, [apiRequest]);

  // Create a new automation
  const handleCreateAutomation = async () => {
    try {
      const defaultAutomation = {
        name: 'New Automation',
        description: '',
        active: true,
        conditions: [],
        actions: []
      };
      
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(defaultAutomation),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const newAutomation = data;
      setAutomations(prevAutomations => [...prevAutomations, newAutomation]);
    } catch (err) {
      console.error('Error creating automation:', err);
      setError('Failed to create automation');
    }
  };

  // Delete an automation
  const handleDeleteAutomation = async (id) => {
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      setAutomations(prevAutomations => prevAutomations.filter(a => a.id !== id));
    } catch (err) {
      console.error('Error deleting automation:', err);
      setError('Failed to delete automation');
    }
  };

  // Toggle an automation's active state
  const handleToggleAutomation = async (id) => {
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${id}/toggle`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const updatedAutomation = data;
      setAutomations(prevAutomations => 
        prevAutomations.map(a => a.id === id ? updatedAutomation : a)
      );
    } catch (err) {
      console.error('Error toggling automation:', err);
      setError('Failed to toggle automation');
    }
  };

  if (loading) {
    return <div className="loading">Loading automations...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="automation-list">
      <div className="automation-list-header">
        <h2>Automations</h2>
        <button 
          className="create-automation-btn" 
          onClick={handleCreateAutomation}
        >
          Create Automation
        </button>
      </div>
      
      {automations.length === 0 ? (
        <div className="no-automations">
          <p>No automations available. Create your first automation!</p>
        </div>
      ) : (
        <div className="automation-cards">
          {automations.map(automation => (
            <AutomationCard 
              key={automation.id} 
              automation={automation} 
              onDelete={handleDeleteAutomation}
              onToggle={handleToggleAutomation}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AutomationList;